import { HttpResponse } from '@angular/common/http';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import {
  BankPricingMode,
  ClosingRateDay,
  EInvoiceAnswerCode,
  EInvoiceTaxScenario,
  LegalStockPricingMode,
  PRICE_ROUNDING_ABSORPTION_LABELS,
  PRICE_ROUNDING_STEP_LABELS,
  PriceRoundingAbsorption,
  PriceRoundingStep,
  StockUnitType,
} from 'src/app/models/enums/e-invoice-enums';
import { LegalStockItemResponse } from 'src/app/models/e-invoice/legal-stock-item';
import { BulkOperationResult } from 'src/app/models/e-invoice/bulk-operation';
import { CreateAutomaticBankDraftsRequestModel } from 'src/app/models/e-invoice/bank-invoice';
import { EInvoiceDocumentLineResponse, EInvoiceLineCalculation } from 'src/app/models/e-invoice/e-invoice-document';
import Swal from 'sweetalert2';

export async function promptAutomaticBankDraftRange(): Promise<CreateAutomaticBankDraftsRequestModel | null> {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const dateValue = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const result = await Swal.fire({
    title: 'Banka taslaklarını oluştur',
    html: `
      <div class="text-start">
        <label class="form-label">Başlangıç tarihi</label>
        <input id="bankDraftStart" type="date" class="form-control mb-3" value="${dateValue(start)}">
        <label class="form-label">Bitiş tarihi</label>
        <input id="bankDraftEnd" type="date" class="form-control mb-3" value="${dateValue(end)}">
        <div class="form-check">
          <input id="bankDraftAll" type="checkbox" class="form-check-input">
          <label class="form-check-label" for="bankDraftAll">Bütün kayıtları işle</label>
        </div>
      </div>`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Taslakları Oluştur',
    cancelButtonText: 'Vazgeç',
    didOpen: () => {
      const all = document.getElementById('bankDraftAll') as HTMLInputElement;
      const startInput = document.getElementById('bankDraftStart') as HTMLInputElement;
      const endInput = document.getElementById('bankDraftEnd') as HTMLInputElement;
      all.addEventListener('change', () => {
        startInput.disabled = all.checked;
        endInput.disabled = all.checked;
      });
    },
    preConfirm: () => {
      const allRecords = (document.getElementById('bankDraftAll') as HTMLInputElement).checked;
      const startDate = (document.getElementById('bankDraftStart') as HTMLInputElement).value;
      const endDate = (document.getElementById('bankDraftEnd') as HTMLInputElement).value;
      if (!allRecords && (!startDate || !endDate)) {
        Swal.showValidationMessage('Tarih aralığını seçin veya bütün kayıtları işaretleyin.');
        return false;
      }
      if (!allRecords && startDate > endDate) {
        Swal.showValidationMessage('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
        return false;
      }
      return { allRecords, startDate: allRecords ? null : startDate, endDate: allRecords ? null : endDate };
    },
  });

  return result.isConfirmed ? result.value as CreateAutomaticBankDraftsRequestModel : null;
}

export async function promptAutomaticIncomingMappingRange(): Promise<{
  allRecords: boolean; startDate: string | null; endDate: string | null;
} | null> {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const dateValue = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const result = await Swal.fire({
    title: 'Akıllı Stok Eşleme',
    html: `
      <div class="text-start">
        <label class="form-label">Başlangıç tarihi</label>
        <input id="incomingMappingStart" type="date" class="form-control mb-3" value="${dateValue(start)}">
        <label class="form-label">Bitiş tarihi</label>
        <input id="incomingMappingEnd" type="date" class="form-control mb-3" value="${dateValue(end)}">
        <div class="form-check">
          <input id="incomingMappingAll" type="checkbox" class="form-check-input">
          <label class="form-check-label" for="incomingMappingAll">Tüm bekleyen belgeleri eşle</label>
        </div>
      </div>`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Eşlemeleri Uygula',
    cancelButtonText: 'Vazgeç',
    didOpen: () => {
      const all = document.getElementById('incomingMappingAll') as HTMLInputElement;
      const startInput = document.getElementById('incomingMappingStart') as HTMLInputElement;
      const endInput = document.getElementById('incomingMappingEnd') as HTMLInputElement;
      all.addEventListener('change', () => {
        startInput.disabled = all.checked;
        endInput.disabled = all.checked;
      });
    },
    preConfirm: () => {
      const allRecords = (document.getElementById('incomingMappingAll') as HTMLInputElement).checked;
      const startDate = (document.getElementById('incomingMappingStart') as HTMLInputElement).value;
      const endDate = (document.getElementById('incomingMappingEnd') as HTMLInputElement).value;
      if (!allRecords && (!startDate || !endDate)) {
        Swal.showValidationMessage('Tarih aralığını seçin veya tüm belgeleri işaretleyin.');
        return false;
      }
      if (!allRecords && startDate > endDate) {
        Swal.showValidationMessage('Başlangıç tarihi bitiş tarihinden sonra olamaz.');
        return false;
      }
      return { allRecords, startDate: allRecords ? null : startDate, endDate: allRecords ? null : endDate };
    },
  });

  return result.isConfirmed
    ? result.value as { allRecords: boolean; startDate: string | null; endDate: string | null }
    : null;
}

// ─── Toplu işlemler ──────────────────────────────────────────────────────────
//
// Muhasebe listelerinde toplu işlem tek bir kalıpla çalışıyor: kullanıcı satırları
// seçer, aksiyonu tetikler, arka uç uygun olmayan kayıtları ATLAR ve ne olduğunu
// id'siyle raporlar. Gerekçe isteyen işlemlerde tek gerekçe bütün seçime yazılır —
// kayıt başına gerekçe toplu işlemin hız kazancını götürürdü.

/** Gerekçe isteyen toplu işlemin (iptal, yok sayma, geri alma) onay penceresi. */
export async function promptBulkReason(options: {
  title: string;
  /** Kaç kaydın ne olacağını anlatan satır — kullanıcı kapsamı buradan görür. */
  description: string;
  confirmButtonText: string;
  placeholder?: string;
}): Promise<string | null> {
  const result = await Swal.fire({
    title: options.title,
    html: `
      <div class="text-start">
        <p class="mb-2">${options.description}</p>
        <label class="form-label">Gerekçe</label>
        <textarea id="bulkReason" class="form-control" rows="3"
                  placeholder="${options.placeholder || 'Gerekçe'}"></textarea>
        <div class="form-text">Aynı gerekçe seçili bütün kayıtlara yazılır.</div>
      </div>`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: options.confirmButtonText,
    cancelButtonText: 'Vazgeç',
    didOpen: () => (document.getElementById('bulkReason') as HTMLTextAreaElement)?.focus(),
    preConfirm: () => {
      const reason = (document.getElementById('bulkReason') as HTMLTextAreaElement).value.trim();
      if (!reason) {
        Swal.showValidationMessage('Gerekçe zorunludur.');
        return false;
      }
      return reason;
    },
  });

  return result.isConfirmed ? result.value as string : null;
}

/** Toplu GİB cevabının kod ve not penceresi. Ret yanıtında not zorunludur. */
export async function promptBulkAnswer(count: number): Promise<{
  answerCode: EInvoiceAnswerCode; note: string | null;
} | null> {
  const result = await Swal.fire({
    title: 'Toplu GİB Cevabı',
    html: `
      <div class="text-start">
        <p class="mb-2">Seçili <strong>${count}</strong> belgeye aynı cevap gönderilecek.
        Cevabı geçmiş belgeler atlanır.</p>
        <label class="form-label">Cevap</label>
        <select id="bulkAnswerCode" class="form-select mb-3">
          <option value="${EInvoiceAnswerCode.Approved}">Kabul</option>
          <option value="${EInvoiceAnswerCode.Rejected}">Ret</option>
        </select>
        <label class="form-label">Not</label>
        <textarea id="bulkAnswerNote" class="form-control" rows="3"
                  placeholder="Ret yanıtında zorunlu"></textarea>
        <div class="form-text">Ret, belgenin stok girişlerini de geri alır.</div>
      </div>`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Cevabı Gönder',
    cancelButtonText: 'Vazgeç',
    preConfirm: () => {
      const answerCode = +(document.getElementById('bulkAnswerCode') as HTMLSelectElement).value;
      const note = (document.getElementById('bulkAnswerNote') as HTMLTextAreaElement).value.trim();

      if (answerCode === EInvoiceAnswerCode.Rejected && !note) {
        Swal.showValidationMessage('Ret yanıtında gerekçe zorunludur.');
        return false;
      }

      return { answerCode, note: note || null };
    },
  });

  return result.isConfirmed
    ? result.value as { answerCode: EInvoiceAnswerCode; note: string | null }
    : null;
}

/**
 * Toplu işlem sonucunun kullanıcıya okunuşu.
 *
 * Atlanan ve başarısız kayıtlar AYRI sayılır: atlama beklenen bir durumdur
 * (karışık seçimde uygun olmayan satır), başarısızlık ise ilgilenilmesi gereken
 * bir hatadır. İkisini tek sayıda toplamak "işlem başarısız" izlenimi verirdi.
 */
export function bulkResultMessages(result: BulkOperationResult | null | undefined): {
  success: string; warning: string | null;
} {
  const succeeded = result?.succeededCount || 0;
  const skipped = result?.skippedCount || 0;
  const failed = result?.failedCount || 0;

  const parts: string[] = [];
  if (skipped) parts.push(`${skipped} kayıt uygun durumda olmadığı için atlandı`);
  if (failed) {
    // İlk hatanın mesajı gösteriliyor: aynı toplu işlemde hatalar genellikle
    // aynı sebepten (entegratör erişimi, eksik ayar) ve liste ekrana sığmaz.
    const first = result?.failures?.[0]?.message;
    parts.push(`${failed} kayıt başarısız${first ? ` — ${first}` : ''}`);
  }
  // Toplu gönderimde alıcı adresi eksik olup kullanıcının onaylamadığı belgeler.
  const unconfirmed = result?.confirmations?.length || 0;
  if (unconfirmed) parts.push(`${unconfirmed} kayıt alıcı adresi eksik olduğu için gönderilmedi`);

  return {
    success: `${succeeded} kayıt işlendi.`,
    warning: parts.length ? `${parts.join(', ')}.` : null,
  };
}

/**
 * E-belge uçlarının hata mesajını çıkarır.
 *
 * ErrorInterceptor global işleyici atlandığında ya düz metin mesaj ya da
 * { message } taşıyan gövdeyi geçiriyor. Entegratör açıklaması bu mesajın
 * içinde geliyor — KIRPILMAZ.
 */
export function extractErrorMessage(error: any, fallback: string): string {
  if (!error) return fallback;

  const message = typeof error === 'string'
    ? error
    : (error.message
      || error.errorMessage
      || (error.error && (error.error.message || error.error.errorMessage)));

  if (typeof message !== 'string' || !message.trim()) return fallback;

  // Mesajsız hatalarda (ör. FailedNotFound yalnızca errorCode döndürüyor)
  // interceptor HttpErrorResponse'un kendi metnini geçiriyor. Bu, kullanıcıya
  // gösterilecek bir açıklama değil; çağıranın verdiği metin daha anlamlı.
  if (/^Http failure/i.test(message.trim())) return fallback;

  return message.trim();
}

/**
 * Ayar girilmemiş veya IsActive değilse arka uç bu mesajı döndürüyor
 * (EInvoiceContext). Ekranlar bunu teknik hata yerine yönlendirici bir kartla
 * göstermeli.
 */
export function isModuleDisabledMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  return message.indexOf('E-belge modülü') > -1
    || (message.indexOf('e-belge') > -1 && message.indexOf('aktif değil') > -1);
}

/** PDF uçlarının döndürdüğü ham baytı yeni sekmede açar. */
export function openPdfResponse(response: HttpResponse<Blob>, fileName: string): void {
  const blob = new Blob([response.body as Blob], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');

  // Açılır pencere engellendiyse indirmeye düşülür; kullanıcı elinde kalsın.
  if (!opened) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  }
}

/**
 * PDF olmayan ekleri (taranmış imzalı nüsha JPEG/PNG/TIFF de olabiliyor) açar.
 * Tür sunucudan geldiği gibi kullanılır; sabit "application/pdf" verilseydi
 * tarayıcı bir JPEG'i bozuk PDF sanardı.
 */
export function openBlobResponse(
  response: HttpResponse<Blob>, fileName: string, contentType: string): void {
  const blob = new Blob([response.body as Blob], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const opened = window.open(url, '_blank');

  if (!opened) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
  }
}

/**
 * PDF isteği hata verdiğinde gövde JSON değil Blob olarak geliyor;
 * mesajı okumak için metne çevirmek gerekiyor.
 */
export function readBlobErrorMessage(error: any, fallback: string): Promise<string> {
  const body = error && (error.error instanceof Blob ? error.error : null);
  if (!body) return Promise.resolve(extractErrorMessage(error, fallback));

  return body.text()
    .then(text => {
      try {
        return extractErrorMessage(JSON.parse(text), fallback);
      } catch {
        return text.trim() || fallback;
      }
    })
    .catch(() => fallback);
}

// ─── Tarih ───────────────────────────────────────────────────────────────────

/** NgbDatepicker değerini API'nin beklediği ISO tarihe çevirir. */
export function toIsoDate(date: NgbDateStruct | null | undefined): string | null {
  if (!date || !date.year) return null;
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');
  return `${date.year}-${month}-${day}`;
}

export function fromIsoDate(value: string | null | undefined): NgbDateStruct | null {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() };
}

export function todayStruct(): NgbDateStruct {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() };
}

// Hızlı gün seçimi ortak tarih aralığı filtresine taşındı; eski içe aktarmalar
// bozulmasın diye buradan da veriliyor.
export { isSameDay, recentDayOptions } from 'src/app/shared/components/date-range-filter/date-range.helpers';
export type { QuickDayOption } from 'src/app/shared/components/date-range-filter/date-range.helpers';

// ─── İşçilik satırı ──────────────────────────────────────────────────────────
//
// Bir yasal stok satırı faturada 1 veya 2 satır üretir: kalemin satış işçiliği
// sıfırsa yalnızca emtia satırı, sıfırdan büyükse emtia satırı + işçilik satırı.
// Karar kalem kartından veriliyor (önizlemeden değil) ki kullanıcı kalemi seçer
// seçmez satır sayısını görsün ve rakamlar geldikçe tablo yerinden oynamasın.

/** İşçilik satırının adını üreten tek yer: "<Mal Hizmet Adı> İşçilik Bedeli". */
export function labourLineNameOf(itemName: string | null | undefined): string {
  const name = (itemName || '').trim();
  return name ? `${name} İşçilik Bedeli` : 'İşçilik Bedeli';
}

// ─── Fatura satırı görünümü ──────────────────────────────────────────────────
//
// Her mal/hizmet satırının işçilik matrahı ekranda AYRI bir satırda durur ve
// miktarı ait olduğu malın miktarıdır — tenant "İşçilik satırını 1 Adet yaz"
// (EInvoiceSetting.labourLineAsSingleUnit) ayarını açtıysa 1 adet. İki kaynak var:
//
// - Arka uç satırı zaten böldüyse (`isLabourLine`) satır olduğu gibi kullanılır;
//   yalnızca miktarı malın miktarına eşitlenir, birim fiyat tutardan türetilir.
// - Bölünmemiş özel matrahlı satırda işçilik matrahı (satış bedeli − has metal
//   bedeli) satırın içinde duruyor. Görünümde mal satırı has bedeline iner,
//   matrah ve KDV'si "<ad> İşçilik Bedeli" satırına taşınır. Toplamlar değişmez.
//
// Sonuç kaynak diziye göre önbelleğe alınır: getter her değişiklik algılamada
// çağrılıyor ve her seferinde yeni nesne dönmek *ngFor'a tabloyu baştan çizdirirdi.

/** Önizleme satırının ekrandaki hâli. */
export interface InvoiceLineView extends EInvoiceLineCalculation {
  /** Görünümü üreten önizleme satırı — silme bununla yapılır. */
  source: EInvoiceLineCalculation;
}

/** Belge satırının ekrandaki hâli. */
export interface DocumentLineView extends EInvoiceDocumentLineResponse {
  /** Görünümü üreten belge satırı — özel matrah kırılımı bununla hesaplanır. */
  source: EInvoiceDocumentLineResponse;
  /** Belgede ayrı satır olarak yazılmamış, işçilik matrahından gösterilen satır. */
  isDerived: boolean;
}

/** UBL "adet" birim kodu — 1 Adet yazılan işçilik satırının birimi. */
export const PIECE_UNIT_CODE = 'C62';

// Önbellek ayara göre ayrı: ayar değişince aynı dizi farklı görünüm üretmeli.
const previewViewCache = [false, true].map(() => new WeakMap<EInvoiceLineCalculation[], InvoiceLineView[]>());
const documentViewCache = new WeakMap<EInvoiceDocumentLineResponse[], DocumentLineView[]>();

/**
 * Önizleme satırlarını her malın işçilik matrahı ayrı satırda olacak şekilde açar.
 *
 * `labourAsSingleUnit`: tenant ayarı. Açıkken işçilik satırı 1 adet, birim fiyatı
 * işçilik tutarının tamamı — sunucu bölünmüş satırı zaten böyle yazıyor, ayrıştırılan
 * (özel matrahın içindeki) işçilik de aynı biçimde gösterilir.
 */
export function invoiceLineViewsOf(
  lines: EInvoiceLineCalculation[] | null | undefined,
  labourAsSingleUnit = false,
): InvoiceLineView[] {
  if (!lines?.length) return [];

  const cache = previewViewCache[labourAsSingleUnit ? 1 : 0];
  const cached = cache.get(lines);
  if (cached) return cached;

  const views: InvoiceLineView[] = [];
  let goods: EInvoiceLineCalculation | null = null;

  lines.forEach((line, index) => {
    if (line.isLabourLine) {
      const owner = goods && (line.legalStockItemId == null || line.legalStockItemId === goods.legalStockItemId)
        ? goods
        : null;
      views.push(labourAsSingleUnit
        ? { ...line, source: owner ?? line }
        : { ...line, quantity: owner?.quantity ?? line.quantity, source: owner ?? line });
      return;
    }

    goods = line;
    const splitByServer = !!lines[index + 1]?.isLabourLine;
    const labour = line.labourAmount ?? 0;

    if (line.error || splitByServer || labour <= 0 || line.specialTaxBaseTotal == null) {
      views.push({ ...line, source: line });
      return;
    }

    views.push({
      ...line,
      lineTotal: line.specialTaxBaseTotal,
      unitPrice: line.quantity > 0 ? line.specialTaxBaseTotal / line.quantity : undefined,
      taxableAmount: 0,
      labourAmount: null,
      kdvAmount: 0,
      source: line,
    });
    views.push({
      ...line,
      name: labourLineNameOf(line.name),
      lineTotal: labour,
      ...(labourAsSingleUnit
        ? { quantity: 1, unitType: PIECE_UNIT_CODE, unitPrice: labour }
        : { unitPrice: line.quantity > 0 ? labour / line.quantity : undefined }),
      hasQuantity: null,
      hasUnitPrice: null,
      specialTaxBaseTotal: null,
      specialTaxBaseReason: null,
      taxableAmount: labour,
      isLabourLine: true,
      source: line,
    });
  });

  cache.set(lines, views);
  return views;
}

/** Tam Düzenle'nin sepet satırına yazılacak fiyatlar (emtia birim fiyatı ve işçilik KDV hariç). */
export interface ManualLinePricing {
  quantity: number;
  unitPrice: number;
  labourAmount: number | null;
}

/**
 * Önizlemenin fiyatlarını sepet satırlarına çevirir — Hızlı Düzenle'den Tam
 * Düzenle'ye geçişte kullanılır.
 *
 * Hızlı Düzenle'de sepet fiyat taşımaz, fiyatları sunucu hedeften türetir. Tam
 * Düzenle ise fiyatı sepetten okur; satırlar olduğu gibi geçseydi emtia birim
 * fiyatı 0 gider ve kart işçiliği "satış bedelinin üzerinde" diye reddedilirdi.
 * Burada kullanıcının ekranda gördüğü fatura, Tam Düzenle'nin başlangıcı olur.
 *
 * Görünümler (`invoiceLineViewsOf`) her malı emtia + işçilik olarak açıyor;
 * sıra sepetle aynı. Önizleme sepetle eşleşmiyorsa ya da hatalı satır varsa null.
 */
export function manualPricingFromPreview(
  views: InvoiceLineView[], basketCount: number): ManualLinePricing[] | null {
  const result: ManualLinePricing[] = [];

  for (let i = 0; i < views.length; i++) {
    const goods = views[i];
    if (goods.isLabourLine) continue;
    if (goods.error || !(goods.quantity > 0)) return null;

    const unitPrice = goods.unitPrice ?? goods.lineTotal / goods.quantity;
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) return null;

    const labour = views[i + 1]?.isLabourLine ? views[i + 1].lineTotal : 0;

    result.push({
      quantity: goods.quantity,
      // Sunucunun fiyatı altı haneye kadar: kesilseydi miktar × fiyat tutmazdı.
      unitPrice: roundTo(unitPrice, UNIT_PRICE_DECIMALS),
      labourAmount: labour > 0 ? roundTo(labour, 2) : null,
    });
  }

  return result.length === basketCount ? result : null;
}

/**
 * Tam Düzenle'de kalan farkı kapatan işçilik: son işçilikli sepet satırı ve
 * yeni işçiliği (KDV hariç); tutmuyorsa null.
 *
 * Hesap önizlemenin işçilik satırından yapılır: matrah sunucunun matrahı
 * (milyem modunda has bedelinin üstündeki her şey), KDV onun yuvarlanmışı.
 * Girilen işçilikteki değişim matrahı birebir kaydırır. İşçilik + KDV her
 * kuruşu üretemediği için (KDV yuvarlaması) tutmayan kalanda null.
 */
export function labourFitForRemaining(
  remaining: number,
  basket: { labourAmount: number | null }[],
  previewLines: EInvoiceLineCalculation[]): { index: number; labour: number } | null {
  const goods = previewLines
    .map((line, index) => line.isLabourLine ? -1 : index)
    .filter(index => index >= 0);
  if (goods.length !== basket.length) return null;

  for (let i = basket.length - 1; i >= 0; i--) {
    const labour = basket[i].labourAmount ?? 0;
    if (!(labour > 0)) continue;

    const labourLine = previewLines[goods[i] + 1];
    if (!labourLine?.isLabourLine) return null;

    const rate = labourLine.kdvRate ?? 0;
    const taxable = labourLine.taxableAmount ?? labourLine.lineTotal;
    const kdv = labourLine.kdvAmount ?? 0;
    const ideal = roundTo(remaining / (1 + rate / 100), 2);

    for (const offset of [0, -0.01, 0.01, -0.02, 0.02, -0.03, 0.03]) {
      const delta = roundTo(ideal + offset, 2);
      const next = roundTo(labour + delta, 2);
      if (next <= 0) continue;
      const kdvAfter = roundTo((taxable + delta) * rate / 100, 2);
      if (roundTo(delta + kdvAfter - kdv, 2) === remaining) return { index: i, labour: next };
    }
    return null;
  }
  return null;
}

/**
 * Belge satırlarını her malın işçilik matrahı ayrı satırda olacak şekilde açar.
 *
 * Belgede ayrı kalem olarak yazılmış işçilik satırı <b>kaydedildiği gibi</b>
 * gösterilir (miktar, birim, birim fiyat): ekran entegratöre gidenle aynı
 * olmalı. Tenant ayarı (labourLineAsSingleUnit) sonradan değişse de eski
 * belgeler kendi satırlarıyla görünür. Yalnızca belgede ayrı kalem olmayan,
 * özel matrahın içinden türetilen işçilik malın miktarını taşır.
 */
export function documentLineViewsOf(lines: EInvoiceDocumentLineResponse[] | null | undefined): DocumentLineView[] {
  if (!lines?.length) return [];

  const cached = documentViewCache.get(lines);
  if (cached) return cached;

  const views: DocumentLineView[] = [];
  let goods: EInvoiceDocumentLineResponse | null = null;

  lines.forEach((line, index) => {
    if (line.isLabourLine) {
      views.push({ ...line, source: line, isDerived: false });
      return;
    }

    goods = line;
    const splitByServer = !!lines[index + 1]?.isLabourLine;
    const hasTotal = line.specialTaxBaseTotal ?? 0;
    const labour = roundTo(line.lineTotal - hasTotal, 2);

    if (splitByServer || hasTotal <= 0 || labour <= 0) {
      views.push({ ...line, source: line, isDerived: false });
      return;
    }

    views.push({
      ...line,
      lineTotal: hasTotal,
      unitPrice: line.quantity > 0 ? (hasTotal + line.discountAmount) / line.quantity : line.unitPrice,
      kdvRate: 0,
      kdvAmount: 0,
      source: line,
      isDerived: false,
    });
    views.push({
      ...line,
      name: labourLineNameOf(line.name),
      description: null,
      milyem: null,
      unitPrice: line.quantity > 0 ? labour / line.quantity : labour,
      discountAmount: 0,
      lineTotal: labour,
      specialTaxBaseTotal: null,
      specialTaxBaseReason: null,
      hasUnitPrice: null,
      isLabourLine: true,
      source: line,
      isDerived: true,
    });
  });

  documentViewCache.set(lines, views);
  return views;
}

/**
 * Kalem kartındaki işçilik değeri.
 *
 * TL modunda birim başına KDV dahil işçilik bedeli; milyem modunda işçilikli
 * satış milyemi ile emtia (satış) milyemi arasındaki fark — 1000 = 1 gram has.
 * İki mod aynı eksende DEĞİL, bu yüzden dönen sayı yalnızca "sıfır mı, değil mi"
 * için kullanılır; işçiliğin TL karşılığı arka uçta hesaplanır
 * (LegalStockPricing.LabourHasPerUnit / LabourAmountPerUnit).
 *
 * Sarrafiyede emtia milyemi kartta boş bırakılabiliyor; o durumda işçilikli
 * milyemin tamamı işçilik sayılır. Karar için yeterli — değer zaten kullanılmıyor.
 *
 * İstisna (KDVK 17/4-g) külçede işçilik yok: kartta doğrulamadan önce
 * kaydedilmiş bir değer kalmış olsa da 0 (arka uç ResolveLabourTotal ile aynı).
 */
export function labourValueOf(item: LegalStockItemResponse | null | undefined): number {
  const value = item?.salesPricingValue ?? 0;
  if (!item || value <= 0 || item.taxScenario === EInvoiceTaxScenario.Exempt) return 0;

  if (item.salesPricingMode === LegalStockPricingMode.Amount) return value;

  const labourMilyem = value - (item.salesMilyem ?? 0);
  return labourMilyem > 0 ? labourMilyem : 0;
}

/** Kalem faturada ayrı bir işçilik satırı üretiyor mu. */
export function hasLabourLine(item: LegalStockItemResponse | null | undefined): boolean {
  return labourValueOf(item) > 0;
}

// ─── Miktar ──────────────────────────────────────────────────────────────────

/**
 * Kalemin birimine göre ondalık basamak sayısı. Gram kalemlerde miktar
 * ondalıklıdır ve has karşılığı hesabına girer; tamsayı zorlamak veri kaybettirir.
 */
export function quantityDecimals(unitType: StockUnitType | null | undefined): number {
  return unitType === StockUnitType.Piece ? 0 : 3;
}

/** Miktar girdisi için adım değeri. */
export function quantityStep(unitType: StockUnitType | null | undefined): string {
  return unitType === StockUnitType.Piece ? '1' : '0.001';
}

/**
 * Kullanıcı girdisini sayıya çevirir. Türkçe klavyede ondalık ayırıcı virgül
 * olduğu için virgül de kabul edilir; gönderilen değer her zaman ham sayıdır.
 */
export function parseDecimal(value: any): number {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return value;
  const normalized = String(value).replace(/\s/g, '').replace(',', '.');
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
}

// ─── Fiyatlama yöntemi ───────────────────────────────────────────────────────

/**
 * Fatura satırlarının nasıl fiyatlandığı. Fatura kesme ve bankadan fatura
 * ekranları aynı üç yöntemi sunuyor; tek fark faturanın çıpası olan tutarın
 * nereden geldiği — kesmede kullanıcı yazıyor, bankada hareketlerin toplamı.
 *
 * `automatic` — kullanıcı yalnızca tutarı verir; emtia, miktar ve birim fiyat
 * e-fatura ayarındaki otomatik kalemden, kapanış kurundan ve kâr oranı bandından
 * arka uçta çözülür. Satır girilmez.
 *
 * `quick` — kalem ve miktar girilir; birim fiyatlar tutardan arka uçta türetilir.
 * Kalem eklendikçe BÜTÜN satırlar yeniden fiyatlanır, sabit olan tek şey toplam.
 *
 * `full` — kalem, miktar, emtia birim fiyatı ve işçilik elle girilir. Hiçbir şey
 * türetilmez; tutar burada bir HEDEF'tir ve "kalan" anlamlıdır.
 */
export type EInvoicePricingMode = 'automatic' | 'quick' | 'full';

/** Ekranın yöntemini banka ucunun beklediği koda çevirir. */
export function bankPricingModeOf(mode: EInvoicePricingMode): BankPricingMode {
  if (mode === 'automatic') return BankPricingMode.Automatic;
  return mode === 'full' ? BankPricingMode.Manual : BankPricingMode.Target;
}

export function roundTo(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/** Birim fiyatın en çok ondalığı — gider pusulasında SUB_036, faturada SUB_035. */
export const UNIT_PRICE_DECIMALS = 6;

/**
 * Tutarı `miktar × birim fiyat` olarak kuruşu kuruşuna veren birim fiyat
 * (arka uçtaki `ExactVoucherUnitPrice` / `NetUnitPricing`). En az ondalıktan
 * başlanır: tam bölünen tutarda fiyat iki haneli kalır, 25.000 ÷ 3 gr
 * = 8.333,333 (üç hane) × 3 = 25.000,00. Altı hane 10.000 birime kadar yeter.
 */
export function exactUnitPrice(amount: number, quantity: number): number {
  if (!(quantity > 0)) return 0;
  const total = roundTo(amount, 2);

  for (let decimals = 2; decimals <= UNIT_PRICE_DECIMALS; decimals++) {
    const price = roundTo(total / quantity, decimals);
    if (roundTo(quantity * price, 2) === total) return price;
  }
  return roundTo(total / quantity, UNIT_PRICE_DECIMALS);
}

// ── Birim fiyat yuvarlaması (birim-fiyat-yuvarlama.md) ─────────────────
//
// Arka uçtaki PriceRounding'in birebir karşılığı: ekran fiyatı kaydetmeden
// ÖNCE adıma oturtur ki kullanıcı sürpriz yaşamasın. Belgenin fiyatını yine
// arka uç belirler; yuvarlama idempotent olduğu için ikisi ayrışmaz.

/** Adımın tutarı; yuvarlama yoksa null. */
export function unitPriceStepSize(step: PriceRoundingStep | null | undefined): number | null {
  switch (step == null ? PriceRoundingStep.None : +step) {
    case PriceRoundingStep.Whole: return 1;
    case PriceRoundingStep.Quarter: return 0.25;
    case PriceRoundingStep.Half: return 0.5;
    default: return null;
  }
}

/**
 * Birim fiyatı adıma yuvarlar — en yakın adım, yarı yukarı: 6.727,125 → 6.727,25.
 * Pozitif fiyat sıfıra inmez (en az bir adım); adım yoksa fiyat olduğu gibi döner.
 */
export function roundUnitPrice(price: number, step: PriceRoundingStep | null | undefined): number {
  const size = unitPriceStepSize(step);
  if (size == null || !(price > 0)) return price;
  // Bölüm önce altı haneye çekilir: 2.400,13 / 0,25 gibi bölümlerde kayan
  // nokta artığı yarım adımı yanlış tarafa düşürmesin.
  const rounded = roundTo(Math.round(roundTo(price / size, 6)) * size, 2);
  return rounded > 0 ? rounded : size;
}

/** Fiyat adımın tam katı mı; adım yoksa her fiyat uygundur. */
export function isOnUnitPriceStep(price: number, step: PriceRoundingStep | null | undefined): boolean {
  const size = unitPriceStepSize(step);
  return size == null || roundUnitPrice(price, step) === roundTo(price, 2);
}

/** Alan altındaki ipucu: "0,25'e yuvarlanır". Yuvarlama yoksa null. */
export function unitPriceRoundingHint(step: PriceRoundingStep | null | undefined): string | null {
  const size = unitPriceStepSize(step);
  if (size == null) return null;
  return +step! === PriceRoundingStep.Whole
    ? 'Birim fiyat tam sayıya yuvarlanır.'
    : `Birim fiyat ${PRICE_ROUNDING_STEP_LABELS[+step!]}'e yuvarlanır.`;
}

/** Önizlemedeki fark notu: "Yuvarlama farkı işçiliğe yazıldı." Fark yoksa null. */
export function roundingAbsorptionNote(kind: PriceRoundingAbsorption | null | undefined): string | null {
  if (kind == null || +kind === PriceRoundingAbsorption.None) return null;
  const target = PRICE_ROUNDING_ABSORPTION_LABELS[+kind];
  return target ? `Yuvarlama farkı ${target} yazıldı.` : null;
}

/**
 * Has fiyatı girilmediğinde kurun sorulacağı gün (ISO): ayardaki kapanış günü,
 * varsayılan belge tarihinin bir gün öncesi (arka uçta ClosingRateDates).
 * Kapanış o gün yoksa sunucu ondan önceki son kapanışa iner.
 */
export function closingRateDateFor(
  issueDate: NgbDateStruct | null | undefined, day: ClosingRateDay | null | undefined,
): string | null {
  const iso = toIsoDate(issueDate);
  if (!iso || day === ClosingRateDay.SameDay) return iso;

  const date = new Date(iso);
  date.setDate(date.getDate() - 1);
  return toIsoDate({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() });
}

/** Kapanış gününün ekran metinleri: "Boşsa {label} kullanılır", "fiyat {from} sabitlenir". */
export const CLOSING_RATE_DAY_TEXT: Record<ClosingRateDay, { label: string; from: string }> = {
  [ClosingRateDay.PreviousDay]: { label: 'bir gün önceki kapanış', from: 'bir gün önceki kapanıştan' },
  [ClosingRateDay.SameDay]: { label: 'aynı günün kapanışı', from: 'aynı günün kapanışından' },
};

// ─── Banka hareketi ödeyen araması ─────────────────────────────────────────

/**
 * Aranan tutarın karşılaştırılabilir yazımı ("1234.56" / "1234"): rakam, nokta
 * ve virgülden oluşmuyorsa null (tutar araması yapılmaz). Türkçe yazımda virgül
 * ondalık, nokta binlik; virgül yoksa "1.234" / "1.234.567" binlik, tek nokta
 * ondalık sayılır ("1234.5").
 */
export function amountSearchKey(term: string | null | undefined): string | null {
  const text = (term || '').replace(/\s|₺|tl/gi, '');
  if (!text || !/^[\d.,]+$/.test(text) || !/\d/.test(text)) return null;

  if (text.includes(',')) {
    const [whole, ...rest] = text.split(',');
    if (rest.length > 1) return null;
    return `${whole.replace(/\./g, '')}.${rest[0]}`;
  }
  if (/^\d{1,3}(\.\d{3})+$/.test(text)) return text.replace(/\./g, '');
  return (text.match(/\./g) || []).length > 1 ? null : text;
}

/**
 * Ödeyen grubu aramaya uyuyor mu. Ad (ödeyen, eşleşen cari kartı, hareketin
 * göndereni — Türkçe büyük/küçük harf duyarsız), VKN/TCKN ve tutar (grubun
 * toplamı ya da tek bir hareketin tutarı). Tutar baştan eşleşir: "1234" yazmak
 * 1.234,56'yı da 12.345,00'ı da bulur — yazarken liste daralsın.
 */
export function bankPayerGroupMatches(
  group: {
    payerName?: string | null;
    payerIdentity?: string | null;
    payerIdentities?: string[] | null;
    matchedCustomerName?: string | null;
    totalAmount: number;
    transactions: { amount: number; senderName?: string | null }[];
  },
  term: string | null | undefined,
): boolean {
  const query = (term || '').trim();
  if (!query) return true;

  const lower = query.toLocaleLowerCase('tr');
  // Müşteriye göre birleşen grupta birden fazla kimlik (IBAN) var; hepsi aranır.
  const identities = group.payerIdentities?.length ? group.payerIdentities : [group.payerIdentity];
  const texts = [
    group.payerName, group.matchedCustomerName, ...identities,
    ...group.transactions.map(t => t.senderName),
  ];
  if (texts.some(text => !!text && text.toLocaleLowerCase('tr').includes(lower))) return true;

  // Kimlik boşluklu yazılmış olabilir ("123 456 78 90").
  const digits = query.replace(/\s/g, '');
  if (/^\d+$/.test(digits)
      && identities.some(identity => (identity || '').replace(/\s/g, '').includes(digits))) return true;

  const amount = amountSearchKey(query);
  if (amount == null) return false;
  return [group.totalAmount, ...group.transactions.map(t => t.amount)]
    .some(value => value != null && value.toFixed(2).startsWith(amount));
}

/** Gram miktarının en çok ondalığı — faturada ve stok hareketinde SUB_038. */
export const QUANTITY_DECIMALS = 6;

/**
 * Birim fiyat sabitken tutarı `miktar × birim fiyat` olarak veren miktar.
 *
 * Miktar YUKARI yuvarlanır: ham çarpım hedefin altında kalmamalı. En yakına
 * yuvarlamak 42,367023 × 6.726,93 = 284.999,998 verir — kuruşa yuvarlanınca
 * 285.000 görünse de satır hedefin altındadır. Yukarı yuvarlanan 42,367024 ×
 * 6.726,93 = 285.000,0047: hedefin üstünde ve kuruşu yine 285.000,00.
 *
 * En az ondalıktan başlanır: tutarı iki haneli miktar veriyorsa miktar iki
 * haneli kalır. Altı hanenin bir adımı × birim fiyat yarım kuruşu aşarsa
 * (birim fiyat 5.000'in üstünde) yukarı yuvarlanan çarpım bir sonraki kuruşa
 * taşabilir; o tutarda hiçbir miktar tutmaz ve null döner.
 */
export function exactQuantity(amount: number, unitPrice: number): number | null {
  if (!(unitPrice > 0)) return null;
  const total = roundTo(amount, 2);
  if (!(total > 0)) return null;

  for (let decimals = 2; decimals <= QUANTITY_DECIMALS; decimals++) {
    const quantity = ceilQuantity(total, unitPrice, decimals);
    if (roundTo(quantity * unitPrice, 2) === total) return quantity;
  }
  return null;
}

/**
 * Çarpımı tutarın altında bırakmayan en küçük miktar (verilen hanede).
 * `exactQuantity` bulamadığında son çare: çarpım bir sonraki kuruşa taşabilir
 * ama tutarın altına inmez.
 */
export function ceilQuantity(amount: number, unitPrice: number, decimals = QUANTITY_DECIMALS): number {
  if (!(unitPrice > 0)) return 0;
  const factor = Math.pow(10, decimals);
  // Kayan nokta artığı (42367023,0000001) bir üst adıma taşımasın diye önce
  // altı haneye yuvarlanıp sonra tavana çıkılır.
  let quantity = roundTo(Math.ceil(roundTo(amount / unitPrice * factor, 6)) / factor, decimals);
  // Tersi: bölmedeki artık tavanı bir adım aşağıda bırakmışsa bir adım çık.
  if (quantity * unitPrice < amount - 1e-7) quantity = roundTo(quantity + 1 / factor, decimals);
  return quantity;
}

/**
 * Kalem kartındaki işçiliğin bu miktardaki <b>KDV hariç</b> TL karşılığı.
 *
 * İki mod iki ayrı eksen — arka uçtaki `LegalStockPricing` ile aynı ayrım:
 * TL modunda değer doğrudan miktarla çarpılır ve gram fiyatı hiç gerekmez;
 * milyem modunda işçilik payı (işçilikli milyem − satış milyemi) has cinsinden
 * olduğu için gram fiyatıyla değerlenir. Kur yoksa milyem modunda karşılık
 * bulunamaz ve null döner — uydurulmuş bir sayı yazmaktansa alan boş kalır.
 *
 * Her iki modda da sonuç KDV HARİÇ matrahtır; KDV üzerine eklenir.
 *
 * İstisna (KDVK 17/4-g) külçede işçilik taşınamaz; kartta ne yazarsa yazsın null.
 */
export function labourGrossFor(
  item: LegalStockItemResponse | null | undefined,
  quantity: number,
  rate: number | null): number | null {
  if (!item || quantity <= 0 || item.taxScenario === EInvoiceTaxScenario.Exempt) return null;

  const value = item.salesPricingValue ?? 0;
  if (value <= 0) return null;

  if (item.salesPricingMode === LegalStockPricingMode.Amount)
    return roundTo(value * quantity, 2);

  const labourMilyem = value - (item.salesMilyem ?? 0);

  return rate != null && labourMilyem > 0
    ? roundTo(labourMilyem / 1000 * quantity * rate, 2)
    : null;
}

/** Kart işçiliğinin birim ve cins olarak okunuşu — işçilik alanının altında gösterilir. */
export function labourCardHint(
  item: LegalStockItemResponse | null | undefined, rate: number | null): string | null {
  const value = item?.salesPricingValue ?? 0;
  if (!item || value <= 0) return null;

  const unit = item.unitType === StockUnitType.Piece ? 'adet' : 'gram';

  if (item.salesPricingMode === LegalStockPricingMode.Amount)
    return `Kart: ${value} ₺/${unit} (KDV hariç)`;

  const labourMilyem = value - (item.salesMilyem ?? 0);
  if (labourMilyem <= 0) return null;

  return rate != null
    ? `Kart: ${labourMilyem} milyem işçilik, güncel kurla değerlendi`
    : `Kart: ${labourMilyem} milyem işçilik — TL karşılığı için gram fiyatı gerekli`;
}

/**
 * "Entegratörde bekleterek gönder" seçeneğinin sağlayıcıdaki karşılığı. Taslak
 * ucu olanda taslak (Nilvera), olmayıp onay bekletmeyi destekleyende onay
 * bekleyerek gönderim (Nettefatura — belge "Onay Bekliyor" olur). İkisi de
 * yoksa null: gönderim her zaman doğrudan. Arka uçtaki öncelik de aynı
 * (EInvoiceManager.SendInternalAsync).
 */
export type ProviderHoldMode = 'draft' | 'approval';

export function providerHoldModeOf(
  capabilities: { supportsProviderDraft?: boolean; supportsSendForApproval?: boolean } | null | undefined,
): ProviderHoldMode | null {
  if (capabilities?.supportsProviderDraft) return 'draft';
  if (capabilities?.supportsSendForApproval) return 'approval';
  return null;
}

/** Bekletme biçimine göre ekran metinleri — ekranlarda sağlayıcı adıyla koşul yazılmaz. */
export const PROVIDER_HOLD_TEXT: Record<ProviderHoldMode, {
  /** "Gönderildiğinde: …" satırı. */
  whenSent: string;
  /** Kısa seçenek adı (Varsayılan (…), liste satırı). */
  short: string;
  /** Fatura bazlı seçimdeki seçenek. */
  option: string;
  /** Fatura bazlı seçimin ipucu. */
  hint: string;
  /** Ayar anahtarının etiketi ve açıklaması. */
  settingLabel: string;
  settingHelp: string;
}> = {
  draft: {
    whenSent: 'entegratörde taslak',
    short: 'Taslak',
    option: 'Taslak olarak gönder',
    hint: 'Taslak seçilirse fatura, \'Gönder\' ile önce entegratörün panelinde taslak olarak oluşturulur; GİB\'e/alıcıya iletmek için detay ekranından ayrıca \'Onayla ve Gönder\' gerekir.',
    settingLabel: 'Entegratöre gönderirken önce taslak oluştur',
    settingHelp: 'Fatura, GİB\'e/alıcıya iletilmeden önce entegratörün kendi panelinde taslak olarak oluşturulur; belge detay ekranından "Onayla ve Gönder" ile kesinleştirilene kadar bekler. Tek tek faturalarda değiştirilebilir.',
  },
  approval: {
    whenSent: 'entegratörde onay bekleyecek',
    short: 'Onay bekleyecek',
    option: 'Onay bekleyecek şekilde gönder',
    hint: 'Fatura entegratöre gönderilir ve portalında onaylanana kadar "Onay Bekliyor" durumunda kalır; GİB\'e/alıcıya onaydan sonra gider. Onay entegratörün portalından verilir, durum kontrolüyle buraya yansır.',
    settingLabel: 'Entegratöre onay bekleyecek şekilde gönder',
    settingHelp: 'Fatura entegratöre gönderilir, portalında onaylanana kadar "Onay Bekliyor" durumunda kalır; GİB\'e/alıcıya onaydan sonra gider. Onay entegratörün portalından verilir ve durum kontrolüyle buraya yansır; portalda reddedilirse fatura "Hata"ya düşer, stok çıkışı geri alınır. Tek tek faturalarda değiştirilebilir.',
  },
};

/**
 * Bu fatura gönderildiğinde entegratörde bekletilecek mi (taslak ya da onay
 * bekleyerek), yoksa doğrudan mı gönderilecek. Belge bazlı override, tenant varsayılanı ve sağlayıcının taslak
 * desteği tek yerde birleştiriliyor ki liste/detay/oluşturma ekranları aynı
 * sonucu göstersin (bkz. EInvoiceManager.ResolveSendAsDraft — arka uçtaki
 * gönderim anındaki gerçek karar da birebir aynı formülü kullanır).
 */
export function willCreateAsProviderDraft(
  sendAsDraftOverride: boolean | null | undefined,
  tenantDefault: boolean,
  providerSupportsDraft: boolean,
): boolean {
  return (sendAsDraftOverride ?? tenantDefault) && providerSupportsDraft;
}
